import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Select,
  SearchableSelect,
  Card,
  Badge,
  getStatusBadgeVariant,
  Pagination,
  EntityLink,
  CompactFilterBar,
  Table,
} from '@/components/common';
import type { Column, SortDirection } from '@/components/common/Table';
import { useQuoteFilterOptions } from '@/hooks';
import { useCan } from '@/hooks/useEmployeeRights';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { useAuthStore } from '@/store';
import { cn, formatRelativeTime, titleCase } from '@/utils';
import type { Quote, QuoteStatus, QuoteBusinessType } from '@/types';
import { useQuoteList, type QuoteListFilters } from './quoteListQuery';
import { QUOTE_STATUS_FILTER_OPTIONS, quoteStatusLabel } from './quoteStatus';

/**
 * M-03: all eleven `QuoteRequest.status` values, not the six that were here.
 *
 * The old list was five legacy values plus `completed`, so `pending`,
 * `quote_shared` and the three KYC rungs -- the statuses the current lifecycle
 * actually puts a quote on -- could not be filtered for at all. The vocabulary
 * is shared with QuoteDetailPage now (`./quoteStatus`), which had all eleven
 * from the start; one list is what stops them drifting apart again.
 */
const statusOptions = QUOTE_STATUS_FILTER_OPTIONS;

const businessTypeOptions = [
  { value: '', label: 'All Business Types' },
  { value: 'new', label: 'New Business' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'rollover', label: 'Rollover' },
  { value: 'portability', label: 'Portability' },
];

const ITEMS_PER_PAGE = 20;

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Flat strings, because that is what `useFilterParams` can decode from a URL;
 * `queryFilters` below turns them back into the shape the API speaks (an empty
 * string means "send nothing" rather than "send blank").
 *
 * DELIBERATELY NOT HERE: the page number. See `PAGE_IS_NOT_PERSISTED` below.
 */
type QuotePanelFilters = {
  search: string;
  insurance_type: string;
  status: string;
  business_type: string;
  /** #471: 'true' while the "Awaiting insurer" chip is on. */
  awaiting_insurer: string;
  /** U-04: the server ordering field, '' when the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: QuotePanelFilters = {
  search: '',
  insurance_type: '',
  status: '',
  business_type: '',
  awaiting_insurer: '',
  sort: '',
  sort_dir: '',
};

/**
 * Q-01 note 3: the `f_` namespace.
 *
 * This page honours NO unprefixed deep-link param today -- nothing in the app
 * links to `/quotes?…`, so there is no seed to read and (STEP 2 of the adoption
 * recipe) nothing for a `consumeSeedParams` helper to consume. The prefix is
 * kept anyway, matching Policies and Contacts, so that the day someone does
 * deep-link a scope in here (`?customer=`, which `QuoteRequestViewSet` already
 * filters on, is the obvious candidate) the page's own filters are already out
 * of the way instead of colliding with it. It costs nothing and the collision
 * is the expensive half to retrofit.
 */
const FILTER_PREFIX = 'f_';

/**
 * U-04: the fields this page offers as sort controls.
 *
 * `QuoteRequestViewSet.ordering_fields` (backend/apps/quotes/views.py:498) is
 * exactly `["created_at", "submitted_at", "request_number"]`. Two of the three
 * are columns here; `submitted_at` is not shown, so there is nothing to put a
 * header on. Customer, Insurance Type, Business Type and Status are NOT
 * orderable server-side, and marking one of them `sortable` would render a
 * control that does nothing: DRF's OrderingFilter drops an unknown term
 * silently and returns the default order, so the click would "work" and the
 * list would come back exactly as it was. Adding a header for any of them
 * needs a backend change first.
 */
const SORTABLE_FIELDS = new Set(['request_number', 'created_at']);

/**
 * PAGE_IS_NOT_PERSISTED.
 *
 * The page number is deliberately kept out of the URL. Every filter change
 * here resets it to 1, and the debounced-search effect re-asserts that on
 * mount -- so a restored page number would be overwritten before the first
 * paint, leaving a URL that says `page=3` over a list showing page 1.
 */

export function QuotesListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link.
  const { filters, setFilter, setFilters, clearAll } = useFilterParams<QuotePanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the box synchronously, `debouncedTerm` (300ms) is what
  // reaches `filters.search` and therefore the query. Seeded from the restored
  // `?f_search=` so a refresh brings the box back filled in, not just the
  // results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  const user = useAuthStore((s) => s.user);

  // U-04: only a field the server can order by, so a stale/hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  // The wire shape. Memoized because it is the react-query key.
  const queryFilters = useMemo<QuoteListFilters>(
    () => ({
      search: filters.search || undefined,
      insurance_type: filters.insurance_type || undefined,
      status: (filters.status || undefined) as QuoteStatus | undefined,
      business_type: (filters.business_type || undefined) as QuoteBusinessType | undefined,
      needs_insurer_pricing: filters.awaiting_insurer === 'true' || undefined,
      ordering,
    }),
    [filters, ordering]
  );

  const { data, isLoading, isError, refetch } = useQuoteList(page, queryFilters);
  // M-02: the Insurance Types filter, on the SAME hook, endpoint and server
  // parameter the customer portal's My Quotes page already uses
  // (`/quotes/requests/filter-options/`, `?insurance_type=`). Deliberately not
  // the platform-wide `useInsuranceTypesLookup` every other page reaches for:
  // this endpoint answers from `QuoteRequestViewSet.get_queryset()`, so the
  // options are the types actually present on quotes THIS caller may see.
  //
  // Which matters because this page is not broker-only. App.tsx also admits
  // `insurer_admin`/`insurer_employee`, and the viewset returns `none()` for an
  // insurer tenant -- so for them this list is legitimately empty. That is a
  // different fact from "the request failed" and from "still loading", and the
  // control below says which of the three it is instead of rendering a silent
  // empty dropdown that reads as an authoritative "there are none".
  const {
    data: filterOptions,
    isLoading: isLoadingFilterOptions,
    isError: filterOptionsFailed,
  } = useQuoteFilterOptions();
  const availableInsuranceTypes = useMemo(
    () => filterOptions?.insurance_types ?? [],
    [filterOptions]
  );
  const insuranceTypeOptions = useMemo(
    () => [
      { value: '', label: 'All Insurance Types' },
      ...availableInsuranceTypes.map((option) => ({ value: option.id, label: option.name })),
    ],
    [availableInsuranceTypes]
  );
  const noInsuranceTypesInScope =
    !isLoadingFilterOptions && !filterOptionsFailed && availableInsuranceTypes.length === 0;

  const quotes = useMemo(() => data?.results ?? [], [data]);
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // The `add_quote` right alone decides (task E2). The hardcoded
  // `role !== 'posp' && role !== 'super_posp'` that used to sit here cited
  // POSP-4 ("POSP quote creation was removed"), but POSP-5 put both roles back
  // on the /quotes/new route on 2026-07-25 (App.tsx) and on the backend gate,
  // so the exclusion contradicted the route AND the rights model: a POSP the
  // broker had granted "Add Quote" could never see the button. Task E1 made
  // POSP rights real (POSPRight, fail-closed), so an ungranted POSP is still
  // hidden — now for the configured reason rather than a stale one.
  const canAddQuote = useCan('quotes', 'create');
  // /quotes/batch is broker-staff only (App.tsx route guard); showing it to an
  // insurer login would just bounce them off an Access Denied screen.
  const canBatchQuote =
    canAddQuote && (user?.role === 'broker_admin' || user?.role === 'broker_employee');

  // Push the debounced term into the filters and reset to the first page in
  // the SAME update, so the query key never carries a new term against a stale
  // page (which used to fire one wrong request and then a second, correct one).
  //
  // Keyed off a ref of the LAST TERM PUSHED, not off `filters.search`: seeded
  // with the restored value it makes the mount pass a no-op, and because Clear
  // resets `filters.search` while `debouncedTerm` still holds the old text for
  // another 300ms, comparing against `filters.search` here would immediately
  // re-apply the search the user just cleared.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilter('search', debouncedTerm);
    setPage(1);
  }, [debouncedTerm, setFilter]);

  const handleFilterChange = (key: keyof QuotePanelFilters, value: string) => {
    setFilters({ [key]: value } as Partial<QuotePanelFilters>);
    setPage(1);
  };

  // U-04: server-side sort. `Table` never reorders the rows it is given -- it
  // reports the next sort state and this sends it as DRF's `?ordering=`, so
  // the order is the whole filtered book's, not this page of twenty.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  const handleRowClick = (quote: Quote) => {
    navigate(`/quotes/${quote.id}`);
  };

  // Count of the PANEL's own filters (search is always visible, so it is
  // excluded; the sort is a view preference, not a filter, so it does not
  // badge either). Badges the Filter toggle and decides whether the panel
  // starts expanded.
  const activeFilterCount = [filters.insurance_type, filters.status, filters.business_type, filters.awaiting_insurer].filter(
    Boolean
  ).length;

  const clearFilters = () => {
    setSearchTerm('');
    // The one action that empties the `?f_*` namespace -- the sort included,
    // so a shared link reproduces the view the sender was looking at. There
    // are no unprefixed deep-link params on this page to consume alongside it.
    clearAll();
    setPage(1);
  };

  /**
   * U-02: the columns, with a width each.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` with `px-6`
   * (48px) and, under `layout="fixed"`, sets `truncate` -- `overflow: hidden`
   * on a border-box cell -- so a column declared at 9rem offers 96px of
   * CONTENT, not 144px, and anything wider is clipped with no scrollbar and no
   * ellipsis. The tight columns override the padding to `px-3` (24px) through
   * `className`; `cn` is tailwind-merge, so a `px-*` there beats the `px-6`:
   *
   *   request_number  14rem  px-6 -> 176px ("QR-2026-0001" over the broker)
   *   customer        14rem  px-6 -> 176px (name over proposer type)
   *   insurance_type  11rem  px-6 -> 128px
   *   business_type    9rem  px-3 -> 120px ("Portability" badge)
   *   status          13rem  px-3 -> 184px (two stacked badges, the widest
   *                                         being "Accepted · awaiting payment")
   *   created_at       9rem  px-3 -> 120px ("about 2 months ago")
   *                  -------
   *                   70rem = 1120px, well inside the ~1424px a 1440-1536px
   *   desktop can show without a horizontal page scrollbar. Narrower than that
   *   the TABLE -- not the page -- scrolls inside its own card.
   *
   * THE CELLS THAT RENDER JSX carry their own `title`. `Table` sets a tooltip
   * only when a cell renders to a string or a number (stringifying arbitrary
   * JSX would show "[object Object]"), so the quote link, the customer link
   * and the badges would otherwise ellipsise with nothing to hover. Each badge
   * additionally gets `max-w-full` with the label in a `truncate` span: an
   * `inline-flex` badge is an atomic inline box, which a `truncate` ancestor
   * hard-CLIPS with no ellipsis at all.
   */
  const columns: Column<Quote>[] = [
    {
      key: 'request_number',
      header: 'Quote',
      width: '14rem',
      // U-04: `request_number` IS in QuoteRequestViewSet.ordering_fields.
      sortable: true,
      render: (quote: Quote) => (
        <div>
          <Link
            to={`/quotes/${quote.id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Open quote ${quote.request_number}`}
            title={quote.request_number}
            className="block truncate text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            {quote.request_number}
          </Link>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      width: '14rem',
      render: (quote: Quote) => (
        <div>
          {/* `kind="contact"` routes to /contacts/<id>, which is a Customer id
              — so this must be `customer_profile`, NOT `customer` (the login
              User id). Every wizard-created customer has a profile and no
              login, so the old field was null exactly when the link mattered.
              The row itself navigates to the quote and that handler fires on
              the way up, so the link has to stop the bubble. */}
          <span
            className="block truncate"
            onClick={(e) => e.stopPropagation()}
            title={quote.customer_name || 'No customer'}
          >
            <EntityLink
              kind="contact"
              id={quote.customer_profile ?? undefined}
              name={quote.customer_name || 'No customer'}
              className="text-sm text-gray-900 hover:underline dark:text-white"
            />
          </span>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {titleCase(quote.proposer_type)}
          </p>
        </div>
      ),
    },
    {
      key: 'insurance_type',
      header: 'Insurance Type',
      width: '11rem',
      // A plain string, so `Table` supplies the tooltip itself.
      render: (quote: Quote) => quote.insurance_type_name,
    },
    {
      key: 'business_type',
      header: 'Business Type',
      width: '9rem',
      className: 'px-3',
      render: (quote: Quote) => (
        <Badge
          variant={quote.business_type === 'new' ? 'primary' : 'info'}
          size="sm"
          className="max-w-full"
        >
          <span className="truncate" title={titleCase(quote.business_type)}>
            {titleCase(quote.business_type)}
          </span>
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '13rem',
      className: 'px-3',
      render: (quote: Quote) => (
        // Each badge sits in its own block wrapper rather than being turned
        // into a block itself: a Badge has to stay `inline-flex` for the
        // `truncate` span inside it to be a FLEX ITEM (blockified, and able to
        // shrink below its content). Overriding the display to `block` would
        // leave the span a plain inline box, where `text-overflow` does
        // nothing and the label is hard-clipped instead.
        <div className="space-y-1">
          <div>
            <Badge variant={getStatusBadgeVariant(quote.status)} size="sm" className="max-w-full">
              {/* M-03: the shared label, not `titleCase` -- which rendered the
                  KYC rungs as "Kyc Pending Approval" and disagreed with both
                  the filter above and the detail page. */}
              <span className="truncate" title={quoteStatusLabel(quote.status)}>
                {quoteStatusLabel(quote.status)}
              </span>
            </Badge>
          </div>
          {/* What the customer did on the shared link. "Payment reported" is
              the one that needs the broker: buy the policy and upload it. */}
          {quote.acceptance_state === 'payment_declared' && (
            <div>
              <Badge variant="success" size="sm" className="max-w-full">
                <span className="truncate" title="Payment reported">
                  Payment reported
                </span>
              </Badge>
            </div>
          )}
          {quote.acceptance_state === 'accepted' && (
            <div>
              <Badge variant="warning" size="sm" className="max-w-full">
                <span className="truncate" title="Accepted · awaiting payment">
                  Accepted &middot; awaiting payment
                </span>
              </Badge>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '9rem',
      className: 'px-3',
      // U-04: `created_at` IS in QuoteRequestViewSet.ordering_fields.
      sortable: true,
      // A plain string, so `Table` supplies the tooltip itself.
      render: (quote: Quote) => formatRelativeTime(quote.created_at),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Quotes"
        subtitle={`${totalItems} total quotes`}
        breadcrumbs={[{ label: 'Quotes' }]}
        actions={
          canAddQuote || canBatchQuote ? (
            <>
              {canBatchQuote && (
                <Link to="/quotes/batch">
                  <Button variant="outline" leftIcon={<RectangleStackIcon className="w-5 h-5" />}>
                    Batch Quotes
                  </Button>
                </Link>
              )}
              {canAddQuote && (
                <Link to="/quotes/new">
                  <Button leftIcon={<PlusIcon className="w-5 h-5" />}>New Quote</Button>
                </Link>
              )}
            </>
          ) : undefined
        }
      />

      {/* One line by default -- search box + Filter toggle badged with the
          active count -- replacing the hand-rolled search row and its
          always-visible "Filters" disclosure. The M-02 Insurance Types control
          moves across as a child of the bar with its id and label unchanged.
          The panel starts expanded whenever a filter is already set, including
          one restored from `?f_*` after a refresh, so nothing is ever filtered
          behind a collapsed panel. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        // B11: the search covers Insurer because the backend's `search_fields`
        // (backend/apps/quotes/views.py) was extended FIRST
        // (`quotes__insurer__name` / `preferred_insurers__name`, proven by
        // `backend/tests/test_b11_quote_search_insurer.py`) -- this placeholder
        // never promises a match the request cannot make.
        searchPlaceholder="Search by quote number, customer, insurer..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      >
        {/* M-02. Three honest states, never two: loading, failed, and "your
            scope has none" each say something different here. Disabled in all
            three, because an open dropdown with nothing in it is the lie this
            is meant to avoid. */}
        <SearchableSelect
          id="quotes-filter-insurance-type"
          label="Insurance Types"
          options={insuranceTypeOptions}
          value={filters.insurance_type}
          onChange={(value) => handleFilterChange('insurance_type', value)}
          placeholder={isLoadingFilterOptions ? 'Loading insurance types…' : 'All Insurance Types'}
          disabled={isLoadingFilterOptions || filterOptionsFailed || noInsuranceTypesInScope}
          error={
            filterOptionsFailed
              ? 'Insurance types could not be loaded. Refresh to try again.'
              : undefined
          }
          helperText={
            noInsuranceTypesInScope ? 'No insurance types on the quotes you can see.' : undefined
          }
        />
        <Select
          id="quotes-filter-status"
          label="Quote Status"
          options={statusOptions}
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        />
        <Select
          id="quotes-filter-business-type"
          label="Business Type"
          options={businessTypeOptions}
          value={filters.business_type}
          onChange={(e) => handleFilterChange('business_type', e.target.value)}
        />
        <div className="flex items-end">
          <button
            type="button"
            id="quotes-filter-awaiting-insurer"
            aria-pressed={filters.awaiting_insurer === 'true'}
            onClick={() =>
              handleFilterChange('awaiting_insurer', filters.awaiting_insurer === 'true' ? '' : 'true')
            }
            className={cn(
              'rounded-full border px-3 py-1.5 text-sm',
              filters.awaiting_insurer === 'true'
                ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
            )}
          >
            Awaiting insurer
          </button>
        </div>
      </CompactFilterBar>

      {/* Quotes table.
          Three states, never two: a failed request, an empty book and a
          loading list are different facts and each has its own branch. The
          failure branch is new — this page used to render "No quotes found"
          over a request that never answered, which is the platform's signature
          defect and is especially misleading here, where an empty list is also
          the HONEST answer for an insurer tenant. */}
      {isLoading ? (
        <Table<Quote> data={[]} columns={columns} isLoading rowKey={(quote) => quote.id} />
      ) : isError && quotes.length === 0 ? (
        <Card padding="none">
          <div role="alert" className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              Could not load quotes.
            </p>
            <button
              type="button"
              className="mt-3 text-sm text-primary-600 underline hover:no-underline dark:text-primary-400"
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        </Card>
      ) : quotes.length === 0 ? (
        <Card padding="none">
          <EmptyState />
        </Card>
      ) : (
        <>
          <Table<Quote>
            data={quotes}
            columns={columns}
            rowKey={(quote) => quote.id}
            onRowClick={handleRowClick}
            // U-02: fixed widths (no column jump when the filters change) and
            // a header that stays put while the rows scroll under it.
            layout="fixed"
            stickyHeader
            // U-04: the Table never reorders anything -- it reports the next
            // sort state and the server does the ordering.
            sortKey={sortKey || undefined}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState() {
  // Same rule as the page header above (task E2): the `add_quote` right decides,
  // with no role exclusion layered on top. The two must agree — a mismatch puts
  // the button back on the empty screen after hiding it on the populated one.
  const canAddQuote = useCan('quotes', 'create');
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No quotes found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Get started by creating a new quote request.
      </p>
      {canAddQuote && (
        <div className="mt-6">
          <Link to="/quotes/new">
            <Button leftIcon={<PlusIcon className="w-5 h-5" />}>New Quote</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
